import axios from 'axios';
import { applyErrorHandlers } from '@/decorators/applyErrorHandler';
import Conversation from '@/models/conversation';

const API_CONVERSATION_URL = process.env.VUE_APP_API_URL + "/conversations";
const ACCESS_TOKEN_NAME_IN_STORAGE = process.env.VUE_APP_ACCESS_TOKEN_NAME_IN_STORAGE;

export default class ConversationController {
  // --- Read endpoints (regular axios; the shared instance carries the auth header) ---

  static async getCurrentUserConversations() {
    const response = await axios.get(`${API_CONVERSATION_URL}/current_user`);
    return (response.data || [])
      .map((conv) => new Conversation(conv))
      .sort((a, b) => ConversationController._recency(b) - ConversationController._recency(a));
  }

  static async getConversation(conversationId) {
    const response = await axios.get(`${API_CONVERSATION_URL}/${conversationId}`);
    return new Conversation(response.data);
  }

  static async getConversationsBySubtask(subtaskId) {
    const response = await axios.get(`${API_CONVERSATION_URL}/by_subtask/${subtaskId}`);
    return (response.data || [])
      .map((conv) => new Conversation(conv))
      .sort((a, b) => ConversationController._recency(b) - ConversationController._recency(a));
  }

  // Synchronous private helper — _-prefixed so applyErrorHandlers leaves it alone;
  // wrapping would make it async and break the sort comparator (#51).
  static _recency(conv) {
    return Date.parse((conv && (conv.updated_at || conv.created_at)) || '') || 0;
  }

  // --- Streaming completion (fetch + ReadableStream, not axios) ---
  //
  // Drives the SSE endpoint POST /conversations/completions. The stream is a series of
  // `data: {json}\n\n` frames. Two frame kinds:
  //   * content chunk: { type:"chunk", role:"assistant", content:"<delta>", ... }
  //   * lifecycle:     { event:"stream_start"|"stream_end", data:"<nested-json-string>" }
  //     (stream_end's nested data carries full_text + conversation_id).
  // Callbacks: onChunk(delta) per content frame, onDone(fullText, conversationId) once
  // at stream end (frame or socket close, whichever comes first), onError(err) on failure.
  //
  // Private streaming core — `_`-prefixed so applyErrorHandlers leaves it UNWRAPPED (#51);
  // the public startCompletion / startTeacherCompletion wrappers below are the wrapped
  // entry points. When onError is supplied we swallow the reject (the caller opted into
  // callback error handling); with no onError we rethrow so the wrapper can normalise the
  // message. `url` selects the student vs teacher endpoint; `systemPrompt` is teacher-only.
  static async _streamCompletion(
    url, { message, conversationId = null, subtaskId = null, systemPrompt = null } = {}, callbacks = {},
  ) {
    const { onChunk, onDone, onError } = callbacks;
    const token = localStorage.getItem(ACCESS_TOKEN_NAME_IN_STORAGE);
    const content = typeof message === 'string' ? message : (message && message.content) || '';
    const body = {
      message: { role: 'user', content: String(content) },
      conversation_id: conversationId || null,
      subtask_id: subtaskId ?? null,
      system_prompt: systemPrompt ?? null,
      stream: true,
    };

    let fullText = '';
    let resolvedConversationId = conversationId || null;
    let doneEmitted = false;

    const emitDone = () => {
      if (doneEmitted) return;
      doneEmitted = true;
      if (onDone) onDone(fullText, resolvedConversationId);
    };

    // Parse one `\n\n`-delimited frame: pull every `data:` line, JSON.parse it, and
    // dispatch. Malformed frames are ignored (never abort the stream on a bad line).
    const handleFrame = (frame) => {
      const text = frame.replace(/^\s+|\s+$/g, '');
      if (!text) return;
      for (const line of text.split(/\r?\n/)) {
        if (!line.startsWith('data:')) continue;
        const payload = line.slice(5).trim();
        if (!payload || payload === '[DONE]') continue;
        let obj;
        try {
          obj = JSON.parse(payload);
        } catch (e) {
          continue;
        }
        if (obj.event) {
          // Lifecycle frame; data is a nested JSON string.
          let inner = obj.data;
          if (typeof inner === 'string') {
            try {
              inner = JSON.parse(inner);
            } catch (e) {
              inner = {};
            }
          }
          inner = inner || {};
          const cid = inner.conversation_id != null ? inner.conversation_id : inner.conversationId;
          if (cid && !resolvedConversationId) resolvedConversationId = String(cid);
          if (obj.event === 'stream_end') {
            if (typeof inner.full_text === 'string' && inner.full_text) fullText = inner.full_text;
            emitDone();
          }
          continue;
        }
        if (typeof obj.content === 'string' && obj.content) {
          fullText += obj.content;
          if (onChunk) onChunk(obj.content);
        }
      }
    };

    try {
      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify(body),
      });

      if (!response.ok || !response.body) {
        throw new Error(`Completion request failed: ${response.status}`);
      }

      const reader = response.body.getReader();
      const decoder = new TextDecoder('utf-8');
      let buffer = '';
      for (;;) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const parts = buffer.split(/\r?\n\r?\n/);
        buffer = parts.pop() || '';
        for (const part of parts) handleFrame(part);
      }
      if (buffer) handleFrame(buffer);
      emitDone();
      return { fullText, conversationId: resolvedConversationId };
    } catch (error) {
      if (onError) {
        onError(error);
        return { fullText, conversationId: resolvedConversationId };
      }
      throw error;
    }
  }

  // Student Ask-AI completion — POST /conversations/completions.
  static async startCompletion({ message, conversationId = null, subtaskId = null } = {}, callbacks = {}) {
    return ConversationController._streamCompletion(
      `${API_CONVERSATION_URL}/completions`,
      { message, conversationId, subtaskId },
      callbacks,
    );
  }

  // Teacher chat completion — POST /conversations/teacher/completions. A subtaskId set ⇒
  // student SQL-tutor preview; unset ⇒ general assistant with an optional systemPrompt.
  static async startTeacherCompletion(
    { message, conversationId = null, subtaskId = null, systemPrompt = null } = {}, callbacks = {},
  ) {
    return ConversationController._streamCompletion(
      `${API_CONVERSATION_URL}/teacher/completions`,
      { message, conversationId, subtaskId, systemPrompt },
      callbacks,
    );
  }
}

applyErrorHandlers(ConversationController);
